// src/components/Stats/Stats.js

import React, { useState, useEffect, useMemo } from 'react';
import './Stats.css';
import LineChart from '../LineChart/LineChart';
import Leaderboard from "../Leaderboard/Leaderboard";
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { formatDayFirstDate } from '../../utils/dateFormatting';
import formatCountLabel from '../../utils/formatCountLabel';

const getLeaderboardPageSize = () => (window.innerWidth <= 600 ? 5 : 10);
const PLAYED_GAME_STATUSES = new Set(['finished', 'settled']);
const PLAYED_GAME_RESULTS = new Set(['win', 'loss', 'draw']);

const getGameStatus = (game) =>
  String(game?.gameId?.status || game?.status || '').toLowerCase();

const getGameResult = (game) => String(game?.result || '').toLowerCase();
const getGameAttendance = (game) => String(game?.attendance || '').toLowerCase();

const getGameDate = (game) => game?.gameId?.date || game?.date;

function Stats({
  refreshTokens,
  handleLogout,
  userProfileOverride = null,
  userGamesOverride = null,
  // Rendered between the points chart and the leaderboard. The profile puts
  // the game history there, now that it no longer has a tab of its own.
  beforeLeaderboard = null,
  leaderboardCurrentUserId = null,
  leaderboardViewerUserId = null,
}) {
  // -----------------------------
  //   State Variables
  // -----------------------------
  const [userProfile, setUserProfile] = useState(null);
  const [userGames, setUserGames] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingGames, setLoadingGames] = useState(true);
  const [error, setError] = useState(null);

  // Derived stats
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [draws, setDraws] = useState(0);
  const [winPercentage, setWinPercentage] = useState(0);
  const [lossPercentage, setLossPercentage] = useState(0);
  const [drawPercentage, setDrawPercentage] = useState(0);

  // Points progression data
  const [pointsData, setPointsData] = useState([]);
  const [dates, setDates] = useState([]);

  // Leaderboard states
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);

  // Server-side pagination/search states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(getLeaderboardPageSize);

  // Optional: If you still want to use filter in your request
  const selectedFilter = 'CITY';

  // Auth token and API
  const API_URL = process.env.REACT_APP_API_URL;
  const canRefreshTokens = typeof refreshTokens === 'function';
  const canHandleLogout = typeof handleLogout === 'function';
  const playedGames = useMemo(
    () =>
      userGames.filter((game) => {
        if (getGameAttendance(game) === 'waitlist') {
          return false;
        }

        const status = getGameStatus(game);
        const result = getGameResult(game);
        return PLAYED_GAME_STATUSES.has(status) || PLAYED_GAME_RESULTS.has(result);
      }),
    [userGames]
  );

  // -----------------------------
  //   Fetch User Profile
  // -----------------------------
  useEffect(() => {
    const handleResize = () => {
      const nextPageSize = getLeaderboardPageSize();
      setItemsPerPage((prev) => {
        if (prev === nextPageSize) return prev;
        setCurrentPage(1);
        return nextPageSize;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // -----------------------------
  //   Fetch User Profile
  // -----------------------------
  useEffect(() => {
    if (userProfileOverride) {
      setUserProfile(userProfileOverride);
      setLoadingProfile(false);
      return;
    }

    const fetchUserProfile = async () => {
      const storedToken = localStorage.getItem('idToken');
      if (!storedToken) {
        setError("No token found, please log in.");
        setLoadingProfile(false);
        return;
      }

      try {
        let response = await fetch(`${API_URL}/profile/user-profile`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storedToken}`,
          },
        });

        // Handle token expiry
        if (response.status === 401 && canRefreshTokens) {
          const tokenRefreshed = await refreshTokens();
          if (tokenRefreshed) {
            response = await fetch(`${API_URL}/profile/user-profile`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
              },
            });
          } else if (canHandleLogout) {
            handleLogout();
            return;
          }
        }

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const data = await response.json();
        setUserProfile(data);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError(err.message);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [API_URL, canHandleLogout, canRefreshTokens, handleLogout, refreshTokens, userProfileOverride]);

  // -----------------------------
  //   Fetch User Games
  // -----------------------------
  useEffect(() => {
    if (Array.isArray(userGamesOverride)) {
      setUserGames(userGamesOverride);
      setLoadingGames(false);
      return;
    }

    const fetchUserGames = async () => {
      const storedToken = localStorage.getItem('idToken');
      if (!storedToken) {
        setError("No token found, please log in.");
        setLoadingGames(false);
        return;
      }

      try {
        let response = await fetch(`${API_URL}/profile/user-games`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storedToken}`,
          },
        });

        if (response.status === 401 && canRefreshTokens) {
          const tokenRefreshed = await refreshTokens();
          if (tokenRefreshed) {
            response = await fetch(`${API_URL}/profile/user-games`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
              },
            });
          } else if (canHandleLogout) {
            handleLogout();
            return;
          }
        }

        if (!response.ok) {
          throw new Error('Failed to fetch user games');
        }

        const data = await response.json();
        setUserGames(data);
      } catch (err) {
        console.error('Error fetching user games:', err);
        setError(err.message);
      } finally {
        setLoadingGames(false);
      }
    };

    fetchUserGames();
  }, [API_URL, canHandleLogout, canRefreshTokens, handleLogout, refreshTokens, userGamesOverride]);

  // -----------------------------
  //   Compute Win/Loss/Draw stats
  // -----------------------------
  useEffect(() => {
    if (playedGames.length > 0) {
      let winsCount = 0, lossesCount = 0, drawsCount = 0;

      playedGames.forEach((game) => {
        const result = getGameResult(game);
        if (result === 'win') winsCount++;
        else if (result === 'loss') lossesCount++;
        else if (result === 'draw') drawsCount++;
      });

      setWins(winsCount);
      setLosses(lossesCount);
      setDraws(drawsCount);

      const totalGames = playedGames.length;
      if (totalGames > 0) {
        setWinPercentage((winsCount / totalGames) * 100);
        setLossPercentage((lossesCount / totalGames) * 100);
        setDrawPercentage((drawsCount / totalGames) * 100);
      } else {
        setWinPercentage(0);
        setLossPercentage(0);
        setDrawPercentage(0);
      }
    } else {
      setWins(0);
      setLosses(0);
      setDraws(0);
      setWinPercentage(0);
      setLossPercentage(0);
      setDrawPercentage(0);
    }
  }, [playedGames]);

  // -----------------------------
  //   Points Progression
  // -----------------------------
  useEffect(() => {
    if (!loadingGames && playedGames.length > 0) {
      const sortedGames = [...playedGames].sort(
        (a, b) => new Date(getGameDate(a)) - new Date(getGameDate(b))
      );

      const pointsArray = [];
      const dateArray = [];
      let cumulativePoints = 0;

      sortedGames.forEach((game) => {
        const points = game.pointsEarned || game.points || 0;
        cumulativePoints += points;
        pointsArray.push(cumulativePoints);

        const date = new Date(getGameDate(game));
        dateArray.push(isNaN(date) ? 'Invalid Date' : formatDayFirstDate(date));
      });

      setPointsData(pointsArray);
      setDates(dateArray);
    } else {
      setPointsData([]);
      setDates([]);
    }
  }, [loadingGames, playedGames]);

  // -----------------------------
  //   Fetch Leaderboard (Paginated)
  // -----------------------------
  useEffect(() => {
    // Wrap in an async function
    const fetchLeaderboard = async () => {
      const storedToken = localStorage.getItem('idToken');

      setLoadingLeaderboard(true);
      try {
        // Build query with page, limit, searchTerm, filter
        const queryParams = new URLSearchParams({
          page: currentPage,
          limit: itemsPerPage,
          filter: selectedFilter,
          searchTerm, // If your backend implements searching
        });

        const headers = {
          'Content-Type': 'application/json',
        };

        if (storedToken) {
          headers.Authorization = `Bearer ${storedToken}`;
        }

        let response = await fetch(`${API_URL}/leaderboard?${queryParams.toString()}`, {
          method: 'GET',
          headers,
        });

        if (response.status === 401 && storedToken && canRefreshTokens) {
          const tokenRefreshed = await refreshTokens();
          if (tokenRefreshed) {
            response = await fetch(`${API_URL}/leaderboard?${queryParams.toString()}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
              },
            });
          } else if (canHandleLogout) {
            handleLogout();
            return;
          }
        }

        if (!response.ok) {
          throw new Error('Failed to fetch leaderboard data');
        }

        // Expect { totalUsers, totalPages, currentPage, data: [...] }
        const json = await response.json();
        setLeaderboardData(json.data || []);  // The chunk of users
        setTotalPages(json.totalPages || 1);
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError(err.message);
      } finally {
        setLoadingLeaderboard(false);
      }
    };

    fetchLeaderboard();
  }, [
    currentPage,
    itemsPerPage,
    searchTerm,
    selectedFilter,
    API_URL,
    canHandleLogout,
    canRefreshTokens,
    refreshTokens,
    handleLogout,
  ]);

  // -----------------------------
  //   Handlers: Search, Pagination
  // -----------------------------
  const handleSearchChange = (newTerm) => {
    setSearchTerm(newTerm);
    setCurrentPage(1); // reset to first page
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // -----------------------------
  //   Loading / Error States
  // -----------------------------
  if (loadingProfile || loadingGames) {
    return <p>Loading your stats...</p>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!userProfile) {
    return <p>Unable to load your profile data.</p>;
  }

  const topScorerCount = Number(userProfile.top_scorer_count) || 0;

  // -----------------------------
  //   Render
  // -----------------------------
  return (
    <div className="stats-container">
      {/* Points Display */}
      <div className="points-display">
        {formatCountLabel(userProfile.points, 'POINT', 'POINTS')}
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-item">
          <CircularProgressbarWithChildren
            value={lossPercentage}
            strokeWidth={3}
            styles={buildStyles({
              textColor: '#1EB62D',
              pathColor: '#1EB62D',
              trailColor: '#0F5E17',
              textSize: '16px',
              strokeLinecap: 'butt',
            })}
          >
            <div className="circle-content">
              <div className="stat-number">{losses}</div>
              <div className="stat-text">LOST</div>
            </div>
          </CircularProgressbarWithChildren>
        </div>
        <div className="stat-item">
          <CircularProgressbarWithChildren
            value={winPercentage}
            strokeWidth={3}
            styles={buildStyles({
              textColor: '#1EB62D',
              pathColor: '#1EB62D',
              trailColor: '#0F5E17',
              textSize: '16px',
              strokeLinecap: 'butt',
            })}
          >
            <div className="circle-content">
              <div className="stat-number">{wins}</div>
              <div className="stat-text">WON</div>
            </div>
          </CircularProgressbarWithChildren>
        </div>
        <div className="stat-item">
          <CircularProgressbarWithChildren
            value={drawPercentage}
            strokeWidth={3}
            styles={buildStyles({
              textColor: '#1EB62D',
              pathColor: '#1EB62D',
              trailColor: '#0F5E17',
              textSize: '16px',
              strokeLinecap: 'butt',
            })}
          >
            <div className="circle-content">
              <div className="stat-number">{draws}</div>
              <div className="stat-text">TIED</div>
            </div>
          </CircularProgressbarWithChildren>
        </div>
      </div>

      {/* Separator */}
      <div className="separator">
        IN YOUR <span className="stat-number">{playedGames.length}</span>{' '}
        {playedGames.length === 1 ? 'GAME' : 'GAMES'}
      </div>

      {/* Goals and Assists Stats */}
      <div className="ga-stats-section">
        <div className="ga-stats-item">
          <div className="stat-label">SCORED</div>
          <div className="stat-value">{userProfile.goals}</div>
          <div className="stat-label">
            {Number(userProfile.goals) === 1 ? 'GOAL' : 'GOALS'}
          </div>
        </div>
        <div className="ga-stats-item">
          <div className="stat-label">GAVE</div>
          <div className="stat-value">{userProfile.assists}</div>
          <div className="stat-label">
            {Number(userProfile.assists) === 1 ? 'ASSIST' : 'ASSISTS'}
          </div>
        </div>
        <div className="ga-stats-item">
          <div className="stat-label">GOT</div>
          <div className="stat-value">{topScorerCount}</div>
          <div className="stat-label">
            TOP SCORER
          </div>
        </div>
      </div>

      {/* Line Chart for Point Progression */}
      {!loadingGames && pointsData.length > 1 ? (
        <div className="point-progress-chart">
          <LineChart dataPoints={pointsData} dates={dates} title="Your Points Over Time" />
        </div>
      ) : (
        <p className="no-points-msg">
          {pointsData.length <= 1
            ? 'Play more games to see your points progression.'
            : 'Loading point progression...'}
        </p>
      )}

      {beforeLeaderboard}

      {/* Leaderboard (server-paginated) */}
      <Leaderboard
        leaderboardData={leaderboardData}
        currentUserId={leaderboardCurrentUserId || userProfile._id}
        viewerUserId={leaderboardViewerUserId || userProfile._id}
        compactNames={itemsPerPage === 5}
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onSearchChange={handleSearchChange}
        searchTerm={searchTerm}
        isLoading={loadingLeaderboard}
        noResultsMessage={searchTerm.trim() ? 'No users found.' : 'No leaderboard data yet.'}
      />
    </div>
  );
}

export default Stats;
